import React from 'react';
import { QRCodeSVG } from 'qrcode.react';

export default function PrintableReceipt({ order, restaurant }) {
    if (!order) return null;

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' });
    };

    const formatTime = (dateString) => {
        const d = new Date(dateString);
        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const subtotal = order?.order_items?.reduce((sum, item) => {
        return sum + (Number(item.price) * Number(item.quantity));
    }, 0) || 0;

    const discount = Number(order?.discount_amount) || 0;
    const tax = Number(order?.tax_amount) || 0;
    const total = Number(order?.total_amount) || (subtotal - discount + tax);
    const isPaid = order?.payment_status === 'paid';

    return (
        <div className="receipt-container" style={{
            width: '80mm',
            padding: '12px',
            fontFamily: "'Courier New', Courier, monospace",
            backgroundColor: '#FFFFFF',
            color: '#000000',
            margin: '0 auto',
            boxSizing: 'border-box'
        }}>
            <style>
                {`
                @media print {
                    @page { margin: 0; size: 80mm auto; }
                    body { margin: 0; padding: 0; }
                    .receipt-container { width: 80mm !important; padding: 12px !important; }
                }
                .dashed-divider {
                    border-top: 1px dashed #000;
                    margin: 8px 0;
                    width: 100%;
                }
                .two-col-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 4px;
                    font-size: 11px;
                }
                .items-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }
                .items-table th, .items-table td {
                    padding: 3px 0;
                }
                `}
            </style>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                {restaurant?.logo_url ? (
                    <img src={restaurant.logo_url} alt="logo" style={{ width: '40px', height: '40px', objectFit: 'contain', marginBottom: '4px' }} />
                ) : (
                    <div style={{ width: '40px', height: '40px', margin: '0 auto 4px', backgroundColor: '#000', borderRadius: '4px' }}></div>
                )}
                <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 2px 0' }}>{restaurant?.name || 'RESTAURANT NAME'}</h1>
                <p style={{ fontSize: '11px', margin: '0 0 2px 0' }}>{restaurant?.address || 'Restaurant Address Line 1'}</p>
                <p style={{ fontSize: '11px', margin: '0' }}>PH: {restaurant?.phone || '+91 0000000000'}</p>
            </div>

            <div className="dashed-divider"></div>

            {/* Order Info */}
            <div className="two-col-grid">
                <div>
                    <p style={{ margin: 0 }}>ID: <strong>#{order.id?.slice(0, 5).toUpperCase()}</strong></p>
                    <p style={{ margin: 0 }}>Date: {formatDate(order.created_at)}</p>
                    <p style={{ margin: 0 }}>Cust: {order.customer_name || 'Guest'}</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0 }}>Time: {formatTime(order.created_at)}</p>
                    <p style={{ margin: 0 }}>Table: {order.table_number || 'N/A'}</p>
                    <p style={{ margin: 0 }}>Type: {order.table_number ? 'Dine-in' : 'Takeaway'}</p>
                </div>
            </div>

            <div className="dashed-divider"></div>

            {/* Items Table */}
            <table className="items-table">
                <thead>
                    <tr style={{ borderBottom: '1px dashed #000' }}>
                        <th style={{ width: '60%', textAlign: 'left' }}>Item</th>
                        <th style={{ width: '15%', textAlign: 'center' }}>Qty</th>
                        <th style={{ width: '25%', textAlign: 'right' }}>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {order.order_items?.map((item, index) => (
                        <tr key={index} style={{ verticalAlign: 'top' }}>
                            <td style={{ textAlign: 'left' }}>{item.name}</td>
                            <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                            <td style={{ textAlign: 'right' }}>₹{Number(item.price) * Number(item.quantity)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="dashed-divider"></div>

            {/* Pricing Section */}
            <div style={{ fontSize: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                    <span>Subtotal:</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <span>Discount:</span>
                        <span>-₹{discount.toFixed(2)}</span>
                    </div>
                )}
                {tax > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                        <span>Tax/GST:</span>
                        <span>₹{tax.toFixed(2)}</span>
                    </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '18px', fontWeight: 'bold' }}>
                    <span>TOTAL:</span>
                    <span>₹{total.toFixed(0)}</span>
                </div>
            </div>

            <div className="dashed-divider"></div>

            {/* Payment Status */}
            <div style={{ 
                textAlign: 'center', 
                fontWeight: 'bold', 
                fontSize: '14px', 
                color: isPaid ? '#16A34A' : '#DC2626',
                margin: '10px 0'
            }}>
                {isPaid ? '*** PAID ***' : '*** PAYMENT PENDING ***'}
            </div>

            <div className="dashed-divider"></div>

            {/* Footer */}
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <p style={{ fontSize: '12px', margin: '0 0 4px 0', fontWeight: 'bold' }}>Thank you for visiting!</p>
                <p style={{ fontSize: '11px', margin: '0 0 2px 0' }}>Get 10% off on your next visit</p>
                <p style={{ fontSize: '11px', margin: '0 0 10px 0' }}>@{restaurant?.instagram || 'yourrestaurant'}</p>
                
                <div style={{ margin: '0 auto', width: '80px', height: '80px' }}>
                    <QRCodeSVG 
                        value={`https://getyourqr.com/m/${restaurant?.id || 'menu'}`} 
                        size={80}
                        level="M"
                        includeMargin={false}
                    />
                </div>
                <p style={{ fontSize: '9px', marginTop: '6px', opacity: 0.8 }}>Scan to view menu or give feedback</p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '12px', fontSize: '10px', fontStyle: 'italic' }}>
                --- Print by GetYourQR ---
            </div>
        </div>
    );
}
